import React, { useEffect, useMemo, useState, useContext } from 'react';

import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { Box, Button, FormControlLabel, Typography } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import trim from 'lodash/trim';

import { PAY_FREQUENCIES, PAYMENT_DAYS } from '../../constants';
import { AppFormWrapper, ImageUploader, PageLoading } from '../../components';
import { SettingsCard, SettingsPage, SettingsSectionConfig } from '../../components/SettingsCard';
import { BankAccountName, BankAccountNumberField, SortCodeField } from '../../components/controlled-inputs';
import { patchHMRCSettingsByCompanyRequest, uploadPayScheduleContractWrapperRequest } from '../../api/pay';
import { CreateOrUpdateCompanyBankAccountRequest, GetCompanyBankAccountRequest } from '../../api/types';
import { getWeekDayName, getWeekDayNumber } from '../../utils/dates';
import { currencyFormatter } from '../../utils';
import { AppCheckbox, AppTextField } from '../../components/inputs';
import { useBooleanState } from '../../hooks';
import { BankAccount, PAY_PERMISSIONS, PaySchedule, PayTagGroup } from '../../types';
import { usePermissions } from '../../hooks/usePermissions';
import { useOutsourcedOnlyCompany } from '../../hooks/useOutsourcedOnlyCompany';
import { getUrlItems } from '../../utils/url';
import { DeviceContext, PayModuleContext } from '../../contexts';

import {
  NotificationStep,
  PayTagsGrid,
  AddExistingContractorDialog,
  HMRCSettingsEditBlock,
  GovernmentGatewayEditBlock,
} from './components';
import { hmrcKeys } from './config';
import { useStyles } from './styles';
import { NotificationContext } from '../../contexts/notificationContext';

interface Props {
  selectedCompanyName?: string;
  isLegend?: boolean;
  /**
   * The company's schedules, when the caller has them. Only used to tell an
   * outsourced-only company from a mixed one — see useOutsourcedOnlyCompany.
   */
  paySchedules?: PaySchedule[];
  getPayScheduleRequest: Function;
  updatePaySchedulesRequest: Function;
  getCompanyBankAccountRequest: (params: GetCompanyBankAccountRequest) => Promise<AxiosResponse>;
  createOrUpdateCompanyBankAccountRequest: (
    params: CreateOrUpdateCompanyBankAccountRequest
  ) => Promise<AxiosResponse>;
}

interface Form extends PaySchedule {
  bank_account: BankAccount;
  tax_office_info: string;
  accounting_office_ref: string;
  utr: string;
  contact_email: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_telephone: string;
  sender_id: string;
  password: string;
  sender: string;
}

const buttonPosition = { top: 16, right: 16 };

export const PaySchedulePage: React.FC<Props> = ({
  isLegend = false,
  selectedCompanyName,
  paySchedules,
  getPayScheduleRequest,
  updatePaySchedulesRequest,
  getCompanyBankAccountRequest,
  createOrUpdateCompanyBankAccountRequest,
}) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [paySchedule, setPaySchedule] = useState<PaySchedule>();
  const { schedule } = useParams<{ schedule: string }>();
  const [companyId, setCompanyId] = useState<string>();
  const [payScheduleId, setPayScheduleId] = useState<string>('');
  const [bankAccount, setBankAccount] = useState<BankAccount>();
  const [loading, setLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [openCards, setOpenCards] = useState<Record<string, boolean>>({});
  const [isAddEmployeeDialogOpen, openAddEmployeeDialog, closeAddEmployeeDialog] = useBooleanState(false);
  const { hmrcSettings } = useContext(PayModuleContext);
  const { isMobile } = useContext(DeviceContext);
  const { can, canOnResource } = usePermissions();
  // Bank details and HMRC settings belong to the company, not to this schedule, so
  // they disappear only when the company has no in-house schedule at all — for the
  // owner too. A mixed company keeps them on every schedule's page.
  const outsourcedOnlyCompany = useOutsourcedOnlyCompany(paySchedules);
  const canManageBankDetails = can(PAY_PERMISSIONS.MANAGE_BANK_DETAILS) && !outsourcedOnlyCompany;
  const canManageHmrc = can(PAY_PERMISSIONS.MANAGE_HMRC_SETTINGS) && !outsourcedOnlyCompany;
  // Editing the schedule is its own right, held per schedule — the Manager role no
  // longer carries it, so saving would 403.
  const canEditPaySchedule = canOnResource(
    PAY_PERMISSIONS.EDIT_PAY_SCHEDULES,
    'pay_schedule',
    paySchedule?.uuid
  );

  const classes = useStyles();
  const methods = useForm<Form>({
    defaultValues: {
      pay_frequency: PAY_FREQUENCIES[0].value as string,
      pay_weekday: '0',
      pay_last_workday: '1',
      email_pay_statement: paySchedule?.email_pay_statement,
      sms_pay_statement: paySchedule?.sms_pay_statement,
      name: '',
      emailWeekday: 'Monday',
      SMSWeekday: 'Monday',
      payroll_fee_base: paySchedule?.payroll_fee_base || undefined,
      payroll_fee_commission: paySchedule?.payroll_fee_commission || undefined,
      bank_account: {
        account_name: '',
        account_number: '',
        sort_code: '',
        building_society_roll_number: undefined,
      },
      tax_office_info: `${hmrcSettings?.tax_office_number || ''} / ${hmrcSettings?.tax_office_ref || ''}`,
      accounting_office_ref: hmrcSettings?.accounting_office_ref || undefined,
      utr: hmrcSettings?.utr || undefined,
      contact_email: hmrcSettings?.contact_email || undefined,
      contact_first_name: hmrcSettings?.contact_first_name || undefined,
      contact_last_name: hmrcSettings?.contact_last_name || undefined,
      contact_telephone: hmrcSettings?.contact_telephone || undefined,
      sender_id: hmrcSettings?.sender_id || undefined,
      password: hmrcSettings?.password || undefined,
      sender: hmrcSettings?.sender || undefined,
      custom_contract: undefined,
      payroll_paid_by_worker: paySchedule?.payroll_paid_by_worker,
    },
  });

  const { control, setValue, watch } = methods;
  const payroll_paid_by_worker = watch('payroll_paid_by_worker');

  const payrollFee = useMemo((): string => {
    if (paySchedule?.payroll_fee_base !== undefined && paySchedule?.payroll_fee_commission !== undefined) {
      const sum = paySchedule?.payroll_fee_base + paySchedule?.payroll_fee_commission;
      return currencyFormatter.format(sum);
    }
    return '';
  }, [paySchedule]);

  const getPaySchedule = async (payScheduleId: string, companyId: string) => {
    try {
      setLoading(true);
      const res = await getPayScheduleRequest(companyId, payScheduleId);
      const bandAccountRes = await getCompanyBankAccountRequest({ companyId });
      setBankAccount(bandAccountRes.data);
      setLoading(false);
      const data = res.data;
      setPaySchedule({
        ...data,
        bank_account: bandAccountRes.data,
        emailWeekday: getWeekDayName(dayjs(data.email_pay_statement).day()),
        SMSWeekday: getWeekDayName(dayjs(data.sms_pay_statement).day()),
      });
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  };

  useEffect(() => {
    const timesheetUrlItems = getUrlItems(schedule);
    const payScheduleId = timesheetUrlItems?.id;
    setPayScheduleId(payScheduleId);
    const companyId = timesheetUrlItems?.additionalId;
    if (companyId) {
      setCompanyId(companyId);
      getPaySchedule(payScheduleId, companyId);
    }
  }, [schedule]);

  const handleSubmit = async (data: Form) => {
    if (!paySchedule?.uuid || !companyId) {
      return;
    }
    try {
      setLoading(true);
      let newHMRCSettings: any = pick(data, hmrcKeys);
      if (data.custom_contract?.file) {
        await uploadPayScheduleContractWrapperRequest({
          payScheduleId: paySchedule.uuid,
          contract: data.custom_contract.file,
          companyId,
        });
      }
      if (!isEqual(data.bank_account, bankAccount)) {
        await createOrUpdateCompanyBankAccountRequest({
          companyId,
          bankAccount: data.bank_account,
        });
      }
      newHMRCSettings = {
        ...newHMRCSettings,
        tax_office_number: trim(newHMRCSettings.tax_office_info?.split('/')[0]) || '',
        tax_office_ref: trim(newHMRCSettings.tax_office_info?.split('/')[1]) || '',
      };

      delete newHMRCSettings.tax_office_info;

      const oldHMRCSettings = {
        ...pick(hmrcSettings, [...hmrcKeys]),
        tax_office_number: trim(hmrcSettings?.tax_office_number) || '',
        tax_office_ref: trim(hmrcSettings?.tax_office_ref) || '',
      };

      if (!isEqual(newHMRCSettings, oldHMRCSettings)) {
        await patchHMRCSettingsByCompanyRequest({
          companyId,
          data: {
            ...newHMRCSettings,
          },
        });
      }
      const res = await updatePaySchedulesRequest(companyId, preparePayScheduleData(data, paySchedule.uuid));
      setPaySchedule({
        ...res.data,
        bank_account: data.bank_account,
      });
      showNotification({ message: 'Pay Schedule has been updated', options: { variant: 'success' } });
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  };

  const preparePayScheduleData = (data: Form, uuid: string): PaySchedule => {
    return {
      uuid,
      name: data.name,
      payroll_fee_base: data.payroll_fee_base,
      payroll_fee_commission: data.payroll_fee_commission,
      payroll_paid_by_worker: !!data.payroll_paid_by_worker,
      email_pay_statement:
        data.email_pay_statement && data.emailWeekday !== undefined
          ? dayjs(data.email_pay_statement).day(getWeekDayNumber(data.emailWeekday)).format()
          : null,
      sms_pay_statement:
        data.sms_pay_statement && data.SMSWeekday !== undefined
          ? dayjs(data.sms_pay_statement).day(getWeekDayNumber(data.SMSWeekday)).format()
          : null,
    };
  };

  const getPaymentDay = (): string => {
    if (paySchedule?.pay_frequency === 'monthly') {
      return paySchedule?.pay_last_workday ? 'Last day of the month' : '28th of every month';
    }
    return PAYMENT_DAYS[paySchedule?.pay_weekday];
  };

  const getAgreementObject = (paySchedule: PaySchedule) => {
    const url = paySchedule?.custom_contract?.body_url;
    return { file: null, url, name: url ? 'agreement.pdf' : '' };
  };

  const notificationStatus = (): string => {
    const sent: string[] = [];
    if (watch('email_pay_statement')) sent.push('Statement email');
    if (watch('sms_pay_statement')) sent.push('Payment text');
    return sent.length ? sent.join(' · ') : 'Nothing sent';
  };

  const hasFees =
    paySchedule?.payroll_fee_base !== undefined || paySchedule?.payroll_fee_commission !== undefined;
  const hasAgreement = !!watch('custom_contract')?.file || !!paySchedule?.custom_contract?.body_url;
  const hasBankDetails = !!watch('bank_account')?.account_number;
  const hasGatewayCredentials = !!watch('sender_id');
  const hasHmrcDetails = !!watch('utr') && !!watch('sender');

  const isOpen = (key: string) => !!openCards[key];
  const setOpen = (key: string) => (expanded: boolean) =>
    setOpenCards(prev => ({ ...prev, [key]: expanded }));

  const sections: SettingsSectionConfig[] = [
    {
      label: 'Schedule',
      rows: [
        {
          cards: [
            {
              key: 'name',
              search: 'name fees base commission payroll fee schedule',
              node: (
                <SettingsCard
                  title={hasFees ? 'Name and fees' : 'Schedule name'}
                  description={
                    hasFees
                      ? 'What this schedule is called across Vyce, and what you are charged for running it.'
                      : 'What this schedule is called across Vyce — workers see it on their pay statements.'
                  }
                  status={watch('name') || paySchedule?.name || 'Unnamed'}
                  expanded={isOpen('name')}
                  onExpandedChange={setOpen('name')}
                  keepMounted
                >
                  <Box maxWidth={520}>
                    <Controller
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <AppTextField
                          {...field}
                          fullWidth
                          value={field.value}
                          onChange={field.onChange}
                          label="Name"
                          inputProps={{ 'cy-test-id': 'pay-schedule-name-field' }}
                        />
                      )}
                    />
                  </Box>

                  {hasFees && (
                    <Box maxWidth={520} display="grid" gridTemplateColumns="1fr 1fr" gridGap={16}>
                      {paySchedule?.payroll_fee_base !== undefined && (
                        <Controller
                          control={control}
                          name="payroll_fee_base"
                          render={({ field }) => (
                            <AppTextField
                              {...field}
                              fullWidth
                              type="number"
                              value={field.value}
                              onChange={field.onChange}
                              label="Base Fee"
                            />
                          )}
                        />
                      )}
                      {paySchedule?.payroll_fee_commission !== undefined && (
                        <Controller
                          control={control}
                          name="payroll_fee_commission"
                          render={({ field }) => (
                            <AppTextField
                              {...field}
                              fullWidth
                              type="number"
                              value={field.value}
                              onChange={field.onChange}
                              label="Commission Fee"
                            />
                          )}
                        />
                      )}
                    </Box>
                  )}
                </SettingsCard>
              ),
            },
            {
              key: 'notifications',
              search: 'notifications pay statement email sms net payment text time weekday',
              node: (
                <SettingsCard
                  title="What workers are sent on payday"
                  description="A text confirming the net payment landing in their account, and an email carrying their pay statement. Set when each goes out, or switch either one off."
                  status={notificationStatus()}
                  statusTone={watch('email_pay_statement') || watch('sms_pay_statement') ? 'on' : 'off'}
                  expanded={isOpen('notifications')}
                  onExpandedChange={setOpen('notifications')}
                  keepMounted
                >
                  <NotificationStep noTitle={true} rowLayout={true} />
                </SettingsCard>
              ),
            },
          ],
        },
      ],
    },
    ...(paySchedule?.outsourced
      ? [
          {
            label: 'Payroll fee',
            rows: [
              {
                cards: [
                  {
                    key: 'feePayer',
                    search: 'payroll fee payer company contractor worker',
                    node: (
                      <SettingsCard
                        title="Who pays the payroll fee"
                        description={`The ${payrollFee} fee for running this payroll. Charge it to the company, or to each contractor.`}
                        status={payroll_paid_by_worker ? 'The contractor' : 'Us, the company'}
                        expanded={isOpen('feePayer')}
                        onExpandedChange={setOpen('feePayer')}
                        keepMounted
                      >
                        <Box marginLeft="-6px" display="flex" flexDirection="column">
                          <FormControlLabel
                            classes={{ label: classes.checkboxLabel }}
                            control={
                              <AppCheckbox
                                onChange={() => setValue('payroll_paid_by_worker', false)}
                                color="primary"
                                cy-test-id="paid-by-company"
                                checked={!payroll_paid_by_worker}
                              />
                            }
                            label="Us, the company"
                          />

                          <FormControlLabel
                            classes={{ label: classes.checkboxLabel }}
                            control={
                              <AppCheckbox
                                onChange={() => setValue('payroll_paid_by_worker', true)}
                                cy-test-id="paid-by-worker"
                                color="primary"
                                checked={!!payroll_paid_by_worker}
                              />
                            }
                            label="The individual contractor"
                          />
                        </Box>
                      </SettingsCard>
                    ),
                  },
                ],
              },
            ],
          },
        ]
      : []),
    ...(paySchedule?.outsourced
      ? []
      : [
          {
            label: 'Payments & HMRC',
            rows: [
              {
                paired: true,
                cards: [
                  // The account workers are paid from — its own permission.
                  ...(canManageBankDetails
                    ? [
                        {
                          key: 'bank',
                          search: 'bank details account number sort code bacs',
                          node: (
                            <SettingsCard
                              title="Bank Details"
                              description="Used to produce your BACS file. Add the details for the account you process payments through — they are not used for anything else."
                              status={hasBankDetails ? 'Set up' : 'Not set up'}
                              statusTone={hasBankDetails ? 'on' : 'off'}
                              expanded={isOpen('bank')}
                              onExpandedChange={setOpen('bank')}
                              keepMounted
                            >
                              <BankAccountName margin="none" />

                              <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap={14}>
                                <BankAccountNumberField margin="none" />
                                <SortCodeField margin="none" />
                              </Box>
                            </SettingsCard>
                          ),
                        },
                      ]
                    : []),
                  {
                    key: 'agreement',
                    search: 'custom agreement contract document upload pdf',
                    node: (
                      <SettingsCard
                        title="Custom Agreement"
                        description="Upload a signed agreement document for this pay schedule."
                        status={hasAgreement ? 'Uploaded' : 'None uploaded'}
                        statusTone={hasAgreement ? 'on' : 'off'}
                        expanded={isOpen('agreement')}
                        onExpandedChange={setOpen('agreement')}
                        keepMounted
                      >
                        <ImageUploader
                          width={200}
                          height={150}
                          image={getAgreementObject(paySchedule as PaySchedule)}
                          isDocument={true}
                          onImageUpload={image => setValue('custom_contract', image)}
                          extraText="document"
                          type="pdf"
                          withEditModeButton
                          buttonPosition={buttonPosition}
                        />
                      </SettingsCard>
                    ),
                  },
                ],
              },
              ...(canManageHmrc
                ? [
                    {
                      paired: true,
                      cards: [
                        {
                          key: 'hmrc',
                          search: 'hmrc details paye reference accounts office utr contact sender agent',
                          node: (
                            <SettingsCard
                              title="HMRC Details"
                              description="In what capacity will you be processing the payroll? E.G. As an Agent on behalf of the employer."
                              status={hasHmrcDetails ? 'Set up' : 'Needs details'}
                              statusTone={hasHmrcDetails ? 'on' : 'off'}
                              expanded={isOpen('hmrc')}
                              onExpandedChange={setOpen('hmrc')}
                              keepMounted
                            >
                              <HMRCSettingsEditBlock />
                            </SettingsCard>
                          ),
                        },
                        {
                          key: 'gateway',
                          search: 'government gateway id password rti credentials',
                          node: (
                            <SettingsCard
                              title="Government Gateway"
                              description="Credentials used to submit RTI to HMRC."
                              status={hasGatewayCredentials ? 'Set up' : 'Not set up'}
                              statusTone={hasGatewayCredentials ? 'on' : 'off'}
                              expanded={isOpen('gateway')}
                              onExpandedChange={setOpen('gateway')}
                              keepMounted
                            >
                              <GovernmentGatewayEditBlock />
                            </SettingsCard>
                          ),
                        },
                      ],
                    },
                  ]
                : []),
            ],
          },
        ]),
    ...(payScheduleId && companyId
      ? [
          {
            label: 'New starter options',
            rows: [
              {
                cards: [
                  {
                    key: 'locations',
                    search: 'starter job locations sites register',
                    node: (
                      <PayTagsGrid
                        title="Job locations for new starters"
                        subtitle="Job locations a new starter can choose from when they register."
                        payScheduleId={payScheduleId}
                        companyId={companyId}
                        group={PayTagGroup.LOCATION}
                        expanded={isOpen('locations')}
                        onExpandedChange={setOpen('locations')}
                      />
                    ),
                  },
                  {
                    key: 'managers',
                    search: 'starter line managers register',
                    node: (
                      <PayTagsGrid
                        title="Line managers for new starters"
                        subtitle="Line managers a new starter can choose from when they register."
                        payScheduleId={payScheduleId}
                        companyId={companyId}
                        group={PayTagGroup.MANAGER}
                        expanded={isOpen('managers')}
                        onExpandedChange={setOpen('managers')}
                      />
                    ),
                  },
                ],
              },
            ],
          },
        ]
      : []),
  ];

  if (loading && !paySchedule) {
    return (
      <Box height="calc(100vh - 240px)">
        <PageLoading />
      </Box>
    );
  }

  return (
    <AppFormWrapper
      methods={methods}
      initialData={paySchedule}
      handleSubmit={handleSubmit}
      isDisabled={!canEditPaySchedule}
      loading={loading}
    >
      {paySchedule && (
        <SettingsPage
          title="Pay schedule"
          description="How this schedule runs — what it is called, what workers hear on payday, and the details HMRC needs."
          sections={sections}
          query={query}
          onQueryChange={setQuery}
          header={
            <Box display="flex" flexDirection="column" gridGap={24}>
              {isLegend && paySchedule?.uuid && companyId && (
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    onClick={openAddEmployeeDialog}
                    variant="outlined"
                    color="primary"
                    fullWidth={isMobile}
                  >
                    Add existing contractor to this pay schedule
                  </Button>

                  <AddExistingContractorDialog
                    open={isAddEmployeeDialogOpen}
                    handleClose={closeAddEmployeeDialog}
                    companyId={companyId}
                    payScheduleId={paySchedule.uuid}
                  />
                </Box>
              )}

              <Box className={classes.summaryStrip}>
                <Box className={classes.summaryCell}>
                  <Typography className={classes.summaryLabel}>Payroll processed via</Typography>
                  <Typography className={classes.summaryValue}>
                    {paySchedule?.outsourced
                      ? 'V Pay Tech HMRC Settings'
                      : `${paySchedule?.company?.name || selectedCompanyName} HMRC Settings`}
                  </Typography>
                </Box>

                <Box className={classes.summaryCell}>
                  <Typography className={classes.summaryLabel}>Pay frequency</Typography>
                  <Typography className={classes.summaryValue}>
                    {capitalize(paySchedule?.pay_frequency)}
                  </Typography>
                </Box>

                <Box className={classes.summaryCell}>
                  <Typography className={classes.summaryLabel}>Payment day</Typography>
                  <Typography className={classes.summaryValue}>{getPaymentDay()}</Typography>
                </Box>
              </Box>
            </Box>
          }
        />
      )}
    </AppFormWrapper>
  );
};
